import React from "react";
import PropTypes from "prop-types";
import {
  useTheme,
  Divider,
  Flex,
  Image,
  Heading,
  Text,
  Box,
  Stack,
  Link,
} from "@chakra-ui/core";
import styled from "@emotion/styled";
import get from "lodash/get";
import Breadcrumbs from "../../components/Breadcrumbs";
import DefaultLayout from "../../components/layouts/DefaultLayout";
import PrimaryButton from "../../components/buttons/PrimaryButton";
import Picture from "../../components/Picture";
import ShareSocialMedia from "../../components/ShareSocialMedia";

const breadcrumbs = ["Orchid", "Resources", "Embryo screening guide"];

export async function getServerSideProps(context) {
  return {
    props: {
      hostname: get(context, "req.headers.host"),
    },
  };
}

function Page({ hostname }) {
  return (
    <DefaultLayout>
      <Flex
        direction="column"
        alignItems="center"
        px={["0", "0", "20px"]}
        py="48px"
        mx="auto"
        maxWidth="1240px"
        w="100%"
      >
        <Flex
          direction="row"
          justifyContent={["center", "center", "space-between"]}
          alignItems="flex-start"
          w="100%"
          mb={["64px", "64px", "0"]}
        >
          <Breadcrumbs alignSelf="flex-start" breadcrumbs={breadcrumbs} />
          <ShareSocialMedia hostname={hostname} />
        </Flex>
        <Flex direction="row" mb="24px">
          <Image h="25px" htmlHeight="15px" w="100px" src="/logo.svg" />
          <Divider mx="10px" orientation="vertical" />
          <Heading fontWeight="300" fontSize="20px" color="orchidBlue500">
            Resources
          </Heading>
        </Flex>
        <Box w="100%" maxW="792px" mx="auto" px={["24px", "0"]}>
          <Heading
            fontSize={["24px", "40px"]}
            fontWeight="300"
            color="orchidBlue500"
            mb="24px"
            textAlign="center"
          >
            How embryo screening can reduce your child’s potential genetic risks
          </Heading>
        </Box>
        <Flex direction="row" mb={["56px", "80px"]}>
          <Text fontSize={["14px", "16px"]} color="orchidGrey500">
            8 min read
          </Text>
          <Divider orientation="vertical" />
          <Text fontSize={["14px", "16px"]} color="orchidPurple">
            Christina Ren
          </Text>
        </Flex>
        <Box w="100%" px={["24px", 0]} mb={["24px", "48px"]}>
          <Picture
            mx="auto"
            imagePath="/images/resources/embryo/header.png"
            alt="embryo report screenshot"
            imgProps={{ style: { margin: "0 auto" } }}
            hasMobile
          />
        </Box>
        <Box w="100%" maxW="792px" mx="auto" px={["24px", "0"]}>
          <Flex
            direction={["column", "row"]}
            alignItems={["center", "flex-start"]}
            mb="43px"
          >
            <Box mr={[0, "48px"]} w={["90px", "128px"]} flexShrink="0">
              <Image
                src="/images/resources/christina.png"
                alt="christina"
                style={{ clipPath: "circle(40%)" }}
              />
            </Box>
            <Flex direction="column">
              <Text
                fontFamily="heading"
                fontWeight="300"
                fontSize={["18px", "20px"]}
                color="orchidGrey700"
                mb="49px"
              >
                In this guide, we’ll cover what embryo screening for common
                conditions can potentially do. Along the way, we’ll teach you
                the basics of the in vitro fertilization (IVF) process. This can
                help you as you’re considering whether embryo screening is the
                right option for you.
              </Text>
              <Flex
                direction={["column", "column", "row"]}
                justifyContent="space-between"
              >
                <Text fontSize={["14px", "16px"]} mb={["18spx", "18px", "0"]}>
                  <Text color="orchidPurple" d="inline">
                    Christina Ren
                  </Text>
                  , Certified Genetic Counselor
                </Text>
                <Box>
                  <Link
                    fontSize="12px"
                    color="orchidGrey500"
                    href="https://twitter.com/RenChristina"
                    target="_blank"
                    rel="noopener noreferrer"
                    mr="24px"
                  >
                    / Twitter
                  </Link>
                  <Link
                    fontSize="12px"
                    color="orchidGrey500"
                    href="https://www.linkedin.com/in/christinaren1/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    / LinkedIn
                  </Link>
                </Box>
              </Flex>
            </Flex>
          </Flex>
          <Divider
            orientation="horizontal"
            borderColor="orchidGrey200"
            mb={["24px", "43px"]}
          />
        </Box>
        <NarrowContainer>
          <StyledHeading>
            What is “embryo screening” and how can it help?
          </StyledHeading>
          <StyledText>
            Embryo screening analyzes the DNA of an embryo to flag genetic risks
            for certain common conditions.
          </StyledText>
          <StyledText>
            Each embryo has differences in its DNA — siblings only share 50
            percent of their DNA with each other, while identical twins have 100
            percent the same DNA. Since each embryo has different DNA, each
            embryo has different genetic risks.
          </StyledText>
          <StyledText>
            When you conceive at home, chance determines your child’s genetics.
            When you create embryos through IVF, you can potentially forecast
            your child’s genetics out of several possible outcomes.
          </StyledText>
          <StyledText>
            As an example, a couple may have two embryos created through IVF
            with different risks for coronary artery disease:
          </StyledText>
        </NarrowContainer>
        <Box w="100%" maxW="824px" mx="auto" bg="white" p="32px" mb="32px">
          <Picture
            imgProps={{
              style: { width: "100%", maxWidth: "583px", margin: "0 auto" },
            }}
            imagePath="/images/resources/embryo/embryo-risk-example.png"
            alt="embryo risk example"
          />
          <Text
            fontSize="12px"
            color="orchidGrey500"
            mt="16px"
            maxW="583px"
            mx="auto"
          >
            *The lifetime risk of coronary artery disease varies based on sex;
            females on average have a 19.2% chance of developing coronary artery
            disease by the age of 80, which is lower than for men{" "}
            <StyledLink href="https://www.ahajournals.org/doi/10.1161/CIRCULATIONAHA.105.548206">
              (Lloyd-Jones, D.M., et al., 2006).
            </StyledLink>
          </Text>
        </Box>
        <NarrowContainer>
          <StyledText>
            So, in this scenario, you can choose to have a child with a lower
            risk of heart disease.
          </StyledText>
          <StyledText>
            If you would like a genetics refresher,{" "}
            <StyledLink href="/resources/what-you-will-learn-from-your-report">
              review our guide on how to think about genetic risk and what it
              means.
            </StyledLink>
          </StyledText>
          <StyledText>
            Besides reporting on genetic risks for common conditions, our embryo
            test also looks at the amount of genetic material, known as
            chromosomes, within each embryo. Identifying embryos with the right
            number of chromosomes increases the chance of IVF success. This is
            often referred to as preimplantation genetic testing for aneuploidy
            (PGT-A).
          </StyledText>
          <StyledText>
            Based on the embryos’ genetic health, patients and their care team
            can select the embryo with the lowest genetic risk and the best
            chance of IVF success. Our embryo report prioritizes each embryo
            according to genetic risk.
          </StyledText>
          <StyledHeading>
            The embryo creation and screening process
          </StyledHeading>
          <Text
            color="orchidGrey500"
            fontWeight={["400", "500"]}
            fontSize={["16px", "24px"]}
          >
            Here’s how it works:
          </Text>
          <Stack>
            <StackItem
              imgUrl="/images/resources/embryo/sperm-egg.png"
              title="IVF"
              text="Embryos are created through IVF. This involves taking medications to prepare the ovaries for egg collection and fertilizing the eggs with sperm to form embryos."
            />
            <StackItem
              imgUrl="/images/resources/embryo/ivf.png"
              title="Embryo Sampling"
              text="Five to ten cells are carefully sampled by a trained embryologist from each embryo. DNA from the sampled cells are analyzed to report on each embryo’s genetic risk."
            />
            <StackItem
              imgUrl="/images/resources/embryo/genetic-report.png"
              title="Genetic Report"
              text="Receive a genetic risk report for each embryo with guidance from a genetics expert."
            />
            <StackItem
              imgUrl="/images/resources/embryo/embryo-transfer.png"
              title="Embryo Transfer"
              text="Prioritize transfer of embryos with the lowest genetic risk when possible and the best chance of success. The remaining embryos can be frozen for future use."
            />
          </Stack>
          <StyledHeading>
            A step-by-step overview of the IVF process
          </StyledHeading>
          <StyledText>
            In vitro fertilization (IVF) involves removing eggs from the ovaries
            and fertilizing them in the laboratory. With embryo screening, an
            average IVF cycle takes about 8 to 10 weeks from the beginning of
            the IVF cycle to embryo transfer. We’ll walk you through the series
            of key steps and what end outcomes to expect.
          </StyledText>
        </NarrowContainer>
        <Box w="100%" maxW="792px" mx="auto" px={["24px", "24px", 0]} mb="48px">
          <Flex alignItems="stretch">
            <TimelineBar
              borderTopLeftRadius="40px"
              borderTopRightRadius="40px"
              backgroundColor="#014793"
            >
              9 - 12 days
            </TimelineBar>
            <Box py={["16px", "32px"]}>
              <TimelineHeading>
                Ovarian stimulation and egg retrieval
              </TimelineHeading>
              <TimelineSubheading>
                Boost egg production through stimulation medications
              </TimelineSubheading>
              <TimelineText mb="16px">
                During a typical menstrual cycle, only one egg is released. In
                one round or “cycle” of IVF, the goal is to grow a larger number
                of follicles, each of which houses an egg, and collect the
                respective number of eggs.
              </TimelineText>
              <TimelineText mb="16px">
                During this time, the patient will inject themselves with
                hormone medications at home and visit the IVF clinic to monitor
                hormone levels through bloodwork and perform ultrasounds to look
                at follicle growth.
              </TimelineText>
              <TimelineSubheading>
                Retrieve eggs from ovaries
              </TimelineSubheading>
              <TimelineText>
                The egg retrieval is about a 20-minute procedure under light
                anesthesia. Once under anesthesia, a fertility doctor will begin
                the procedure by using ultrasound to guide a small needle,
                through the vaginal wall, into each ovary to collect the eggs.
                The eggs are then transferred to the specialized embryology lab.
              </TimelineText>
            </Box>
          </Flex>
          <Flex alignItems="stretch">
            <TimelineBar backgroundColor="rgba(1, 71, 147, 0.8)">
              5 - 7 days
            </TimelineBar>
            <Box py={["16px", "32px"]}>
              <TimelineHeading color="rgba(1, 71, 147, 0.8)">
                Embryo Transfer
              </TimelineHeading>
              <TimelineSubheading>
                Create embryos by fertilizing eggs with sperm
              </TimelineSubheading>
              <TimelineText mb="32px">
                On the day of egg retrieval, the mature eggs are fertilized with
                sperm by either surrounding eggs by sperm for one sperm to
                fertilize the egg or selecting a single sperm to inject directly
                into the egg. The next day, embryologists check how many mature
                eggs have fertilized normally.
              </TimelineText>
              <TimelineSubheading mb="16px">Grow Embryos</TimelineSubheading>
              <TimelineText>
                Behind the scenes, embryos grow and develop in an incubator that
                mimics the environment found in a woman’s fallopian tubes. Many
                clinics grow embryos until they reach the blastocyst stage,
                which comprises over a hundred or so cells.
              </TimelineText>
            </Box>
          </Flex>
          <Flex alignItems="stretch">
            <TimelineBar backgroundColor="rgba(1, 71, 147, 0.6)">
              10 - 14 days
            </TimelineBar>
            <Box py={["16px", "32px"]}>
              <TimelineSubheading>
                Sample embryos for genetic testing and freeze embryos
              </TimelineSubheading>
              <TimelineText mb="16px">
                The embryos that have reached the blastocyst stage by day 5 or 6
                are sampled (biopsied) and the sampled cells are sequenced and
                analyzed.
              </TimelineText>
              <TimelineText>
                Embryo screening results take about 10-14 days once the embryo
                samples are sent. In the meantime, the embryos are frozen and
                kept safely in the IVF clinic.
              </TimelineText>
            </Box>
          </Flex>
          <Flex alignItems="stretch">
            <TimelineBar
              borderBottomLeftRadius="40px"
              borderBottomRightRadius="40px"
              backgroundColor="rgba(1, 71, 147, 0.4)"
            >
              Variable
            </TimelineBar>
            <Box py={["16px", "32px"]}>
              <TimelineSubheading>
                Transfer of embryos into the uterus
              </TimelineSubheading>
              <TimelineText mb="16px">
                Typically frozen embryos are thawed and transferred back to the
                uterus after the second menstrual period following egg
                retrieval. This stage depends on the patient&apos;s natural
                menstrual cycle length and when they decide to transfer.
              </TimelineText>
              <TimelineText>
                The embryo transfer procedure is not painful and takes a few
                minutes. The patients are able to watch by ultrasound as embryos
                are transferred. A follow-up pregnancy test is done about 9 to
                11 days after.
              </TimelineText>
            </Box>
          </Flex>
        </Box>
        <NarrowContainer>
          <StyledHeading>
            How safe is the ovarian stimulation and egg retrieval process? Are
            there any side effects? Will it hurt?
          </StyledHeading>
          <StyledText>
            Some patients may experience bloating, light vaginal spotting and
            abdominal cramping from the hormone medications similar to a
            menstrual period. These effects usually subside a few days after egg
            retrieval. Some women may be at risk of ovarian hyperstimulation
            syndrome (OHSS). OHSS can be largely avoided and eliminated with
            close monitoring and medication dosage adjustments. Talk to your
            fertility doctor to see if you may be at risk.
          </StyledText>
          <StyledText>
            Recovery from the egg retrieval procedure is brief and patients can
            return to routine daily activities over the next few days, usually
            next day. Most patients walk out of the clinic after the procedure
            about 45 minutes later, taking the rest of the day off to rest.
          </StyledText>
          <StyledHeading>
            If I decide to do this, how many transferable embryos will I expect
            to get from one IVF cycle?
          </StyledHeading>
          <StyledText>
            The number of embryos you can get depends on your age, genetics and
            whether there are underlying causes of infertility. On average,
            women in their late 20s can expect about 4 to 5 chromosomally normal
            (euploid) embryos per cycle (
            <StyledLink href="https://www.sciencedirect.com/science/article/pii/S0015028216000662">
              Demko et al., 2016
            </StyledLink>
            ). This number decreases with increasing maternal age.
          </StyledText>
          <StyledText>
            It is typically recommended to transfer only one chromosomally
            normal embryo at a time (
            <StyledLink href="https://www.reproductivefacts.org/news-and-publications/patient-fact-sheets-and-booklets/documents/fact-sheets-and-info-booklets/why-would-i-choose-to-have-elective-single-embryo-transfer-eset/?_ga=2.229144412.1573637278.1603151184-1356667362.1602195094">
              ASRM patient fact sheet, Lee et al., 2016
            </StyledLink>
            ). You can keep any remaining embryos frozen for future use.
          </StyledText>
          <StyledText>
            Before starting IVF treatment, fertility doctors typically use
            bloodwork and baseline ultrasounds to get an idea of how many eggs
            they might anticipate to be able to retrieve. However, it is
            important to know that eggs and embryos are naturally lost along the
            way; you do not end up with the same number of embryos as the number
            of eggs you retrieved.
          </StyledText>
          <StyledText>
            To illustrate this, we’ve outlined some hypothetical numbers in the
            embryo creation process for a 28-year-old woman. Remember these are
            based on averages and can go up or down depending on each
            individual.
          </StyledText>
        </NarrowContainer>
        <Picture
          mx="auto"
          mb="32px"
          imagePath="/images/resources/embryo/sample-cycle.png"
          alt="sample cycle"
          hasMobile
        />
        <NarrowContainer>
          <StyledHeading>
            Will this be enough to mitigate genetic risks for a specific
            condition?
          </StyledHeading>
          <StyledText>
            While we cannot guarantee there will be at least one embryo with
            normal genetic risks for all conditions tested, embryo screening is
            able to substantially reduce potential risks. This is especially
            true if your Orchid Couple Report shows that your future child has
            an increased genetic predisposition to a specific condition.
          </StyledText>
          <StyledText>
            The genetic makeup of each embryo created during IVF is determined
            by chance, but we provide you with the most advanced tools available
            to quantify genetic susceptibility and have the healthiest child
            possible.
          </StyledText>
          <StyledHeading>Additional resources about IVF</StyledHeading>
          <Stack spacing="32px" mb="48px" alignItems="flex-start">
            <StyledLink href="https://www.asrm.org/resources/patient-resources/">
              American Society of Reproductive Medicine (ASRM Patient Resources)
            </StyledLink>
            <StyledLink href="https://www.reproductivefacts.org/news-and-publications/patient-fact-sheets-and-booklets/">
              Society of Assisted Reproductive Technology (SART)
            </StyledLink>
            <StyledLink href="https://www.acog.org/en/Womens%20Health/FAQs/Evaluating%20Infertility">
              American College of Obstetricians and Gynecologists (ACOG)
            </StyledLink>
            <StyledLink href="https://www.fertilityiq.com/ivf-in-vitro-fertilization/the-ivf-process">
              FertilityIQ
            </StyledLink>
          </Stack>
          <StyledHeading fontSize={["20px", "20px", "32px"]}>
            Orchid offers advanced genetic testing for couples planning on
            building their family. We use advanced tools and smart, caring
            humans to help you give your future children the best shot of a
            healthy life.
          </StyledHeading>
          <StyledHeading fontSize={["20px", "20px", "32px"]}>
            Conceive with greater confidence and peace of mind.
          </StyledHeading>
          <Link href="https://portal.orchidhealth.com/purchase">
            <PrimaryButton fontWeight="500" py="16px" display="block" mx="auto">
              Get Orchid
            </PrimaryButton>
          </Link>
        </NarrowContainer>
      </Flex>
    </DefaultLayout>
  );
}

const StackItemContainer = styled(Flex)`
  border-bottom: 1px solid ${(props) => props.theme.colors.black08};
  padding-top: 32px;
  padding-bottom: 32px;

  &:last-of-type {
    border-bottom: 0;
  }
`;

const StackItem = ({ imgUrl, title, text }) => {
  const theme = useTheme();

  return (
    <StackItemContainer theme={theme} direction={["column", "row"]}>
      <Box
        w="120px"
        mr={["auto", "8px"]}
        ml={["auto", null]}
        mb="8px"
        flexShrink="0"
      >
        <Image mx="auto" src={imgUrl} alt="sperm-egg" />
      </Box>
      <Box>
        <Heading
          textAlign={["center", "left"]}
          fontWeight="300"
          fontSize="20px"
          mr="8px"
          color="orchidBlue500"
          mb="14px"
        >
          {title}
        </Heading>
        <StyledText>{text}</StyledText>
      </Box>
    </StackItemContainer>
  );
};

StackItem.propTypes = {
  imgUrl: PropTypes.string,
  title: PropTypes.string,
  text: PropTypes.string,
};

const TimelineBar = ({ children, backgroundColor, ...rest }) => (
  <Flex
    justifyContent="space-around"
    alignItems="center"
    w="60px"
    backgroundColor={backgroundColor}
    mr="24px"
    display={["none", "flex"]}
    {...rest}
  >
    <Text
      color="white"
      transform="rotate(-90deg)"
      fontSize="28px"
      fontWeight="bold"
      whiteSpace="nowrap"
    >
      {children}
    </Text>
  </Flex>
);

TimelineBar.propTypes = {
  children: PropTypes.node,
  backgroundColor: PropTypes.string,
};

const TimelineHeading = ({ children, ...rest }) => (
  <Text
    mb="32px"
    color="#014793"
    fontSize={["25px", "32px"]}
    lineHeight={["20px", "40px"]}
    fontWeight="700"
    {...rest}
  >
    {children}
  </Text>
);

TimelineHeading.propTypes = {
  children: PropTypes.node,
};

const TimelineSubheading = ({ children, ...rest }) => (
  <Text
    mb="16px"
    color="orchidBlue"
    fontSize={["18px", "28px"]}
    lineHeight={["23px", "25px"]}
    fontWeight="400"
    {...rest}
  >
    {children}
  </Text>
);

TimelineSubheading.propTypes = {
  children: PropTypes.node,
};

const TimelineText = ({ children, ...rest }) => (
  <Text
    fontSize={["16px", "18px"]}
    lineHeight={["20px", "28px"]}
    fontWeight="400"
    color="orchidGrey700"
    {...rest}
  >
    {children}
  </Text>
);

TimelineText.propTypes = {
  children: PropTypes.node,
};

const StyledLink = ({ children, ...rest }) => (
  <Link
    color="#3a8370"
    textDecoration="underline"
    target="_blank"
    rel="noopener noreferrer"
    {...rest}
  >
    {children}
  </Link>
);

StyledLink.propTypes = {
  children: PropTypes.node,
};

const StyledText = ({ children, ...rest }) => (
  <Text
    mb={["24px", "32px"]}
    fontSize={["16px", "18px"]}
    lineHeight="32px"
    color="orchidGrey500"
    {...rest}
  >
    {children}
  </Text>
);

const StyledHeading = ({ children, ...rest }) => (
  <Heading
    mb={["16px", "32px"]}
    fontSize={["20px", "32px"]}
    fontWeight="300"
    color="orchidBlue"
    {...rest}
  >
    {children}
  </Heading>
);

const NarrowContainer = ({ children, ...rest }) => (
  <Box w="100%" maxW="612px" mx="auto" px={["24px", "24px", "0"]} {...rest}>
    {children}
  </Box>
);

StyledText.propTypes = { children: PropTypes.node };
StyledHeading.propTypes = { children: PropTypes.node };
NarrowContainer.propTypes = { children: PropTypes.node };

Page.propTypes = {
  hostname: PropTypes.string,
};

export default Page;
